import { FormikProps } from 'formik';

import { forceShowFormValidationErrors } from './forceShowFormValidationErrors';

export type SoftContinueSession = {
  proceedDespiteErrorsAcknowledged: boolean;
  valuesSnapshotAtAcknowledge: string | undefined;
};

export function createSoftContinueSession(): SoftContinueSession {
  return { proceedDespiteErrorsAcknowledged: false, valuesSnapshotAtAcknowledge: undefined };
}

function serializeFormValuesForSnapshot(values: unknown): string {
  return JSON.stringify(values);
}

function clearAcknowledgeIfValuesChanged(session: SoftContinueSession, values: unknown) {
  if (!session.proceedDespiteErrorsAcknowledged) {
    return;
  }
  if (session.valuesSnapshotAtAcknowledge === undefined) {
    return;
  }
  const current = serializeFormValuesForSnapshot(values);
  if (current !== session.valuesSnapshotAtAcknowledge) {
    session.proceedDespiteErrorsAcknowledged = false;
    session.valuesSnapshotAtAcknowledge = undefined;
  }
}

function acknowledgeProceedDespiteErrors(session: SoftContinueSession, values: unknown) {
  session.proceedDespiteErrorsAcknowledged = true;
  session.valuesSnapshotAtAcknowledge = serializeFormValuesForSnapshot(values);
}

function resetSoftContinueAcknowledge(session: SoftContinueSession) {
  session.proceedDespiteErrorsAcknowledged = false;
  session.valuesSnapshotAtAcknowledge = undefined;
}

export async function runSoftFormStepContinue<T>(
  formik: FormikProps<T> | null | undefined,
  onStepComplete: (values: T) => void,
  session: SoftContinueSession,
): Promise<boolean> {
  if (!formik) {
    return false;
  }

  clearAcknowledgeIfValuesChanged(session, formik.values);

  const hasErrors = await forceShowFormValidationErrors(formik);

  if (hasErrors) {
    if (!session.proceedDespiteErrorsAcknowledged) {
      acknowledgeProceedDespiteErrors(session, formik.values);
      return false;
    }

    resetSoftContinueAcknowledge(session);
  } else {
    resetSoftContinueAcknowledge(session);
  }

  onStepComplete(formik.values);
  return true;
}

export type SoftFormStep<T> = {
  formik: FormikProps<T> | null | undefined;
  onStepComplete: (values: T) => void;
};

export async function runSoftMultiFormStepContinue(
  steps: Array<SoftFormStep<unknown>>,
  session: SoftContinueSession,
): Promise<boolean> {
  if (steps.length === 0 || steps.some((step) => !step.formik)) {
    return false;
  }

  const combinedValues = steps.map((step) => step.formik!.values);
  clearAcknowledgeIfValuesChanged(session, combinedValues);

  const validationResults = await Promise.all(
    steps.map(async (step) => {
      const formik = step.formik!;
      const hasErrors = await forceShowFormValidationErrors(formik);
      return { hasErrors };
    }),
  );

  const anyErrors = validationResults.some((result) => result.hasErrors);

  if (anyErrors) {
    if (!session.proceedDespiteErrorsAcknowledged) {
      acknowledgeProceedDespiteErrors(session, combinedValues);
      return false;
    }
    resetSoftContinueAcknowledge(session);
  } else {
    resetSoftContinueAcknowledge(session);
  }

  for (const step of steps) {
    step.onStepComplete(step.formik!.values);
  }

  return true;
}
